import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Badge from '../ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { isWhiteLabel } from '../../config/tenantConfig';
import LoadingSpinner from '../common/LoadingSpinner';
import { useToast } from '../common/ToastContainer';
import { Plus, Trash2, RefreshCw, Save, X, Pencil } from 'lucide-react';
import { BrokerAccount, Basket, BasketBrokerAllocation, CreateAllocation } from '../../types';
import brokerService from '../../services/brokerService';
import allocationService from '../../services/allocationService';
import strategyService from '../../services/strategyService';
import EnableToggle from '../ui/EnableToggle';
import { ALLOCATION_STATUS } from '../../constants/tradingConstants';

const MAX_EFFECTIVE_LOTS_PER_LEG = 200;

interface BasketAllocationProps {
  basket: Basket;
  onAllocationComplete: () => void;
}

interface AllocationFormData {
  broker_id: string;
  client_id: string;
  lot_multiplier: number;
}

const BasketAllocation: React.FC<BasketAllocationProps> = ({
  basket,
  onAllocationComplete
}) => {
  const wl = isWhiteLabel();
  const { showSuccess, showError } = useToast();
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccount[]>([]);
  const [existingAllocations, setExistingAllocations] = useState<BasketBrokerAllocation[]>([]);
  const [newAllocations, setNewAllocations] = useState<AllocationFormData[]>([]);
  // Largest leg.lots across all strategies in this basket — drives lot_multiplier cap.
  const [maxLegLots, setMaxLegLots] = useState<number>(0);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit mode state
  const [editingAllocation, setEditingAllocation] = useState<string | null>(null);
  const [editLotMultiplier, setEditLotMultiplier] = useState<number>(1);

  // Load broker accounts and existing allocations
  const loadData = async () => {
    try {
      setLoading(true);

      // Load broker accounts
      const accounts = await brokerService.getBrokerAccounts();
      setBrokerAccounts(accounts.filter(account => account.account_status === 'enabled'));

      // Load existing allocations for this basket
      try {
        const allocations = await allocationService.getBasketAllocations(basket.basket_id);
        setExistingAllocations(allocations);
      } catch (allocError) {
        console.warn('Failed to load existing allocations:', allocError);
        // Continue without existing allocations - user can still create new ones
        setExistingAllocations([]);
      }

      // Load basket strategies to compute max(leg.lots) — this caps lot_multiplier
      // so that lot_multiplier × leg.lots stays within MAX_EFFECTIVE_LOTS_PER_LEG.
      try {
        const strategies = await strategyService.getBasketStrategies(basket.basket_id);
        const max = strategies.reduce(
          (m, s) => Math.max(m, s.maxLegLots ?? 0),
          0,
        );
        setMaxLegLots(max);
      } catch (stratError) {
        console.warn('Failed to load basket strategies for lot cap:', stratError);
        setMaxLegLots(0);
      }

    } catch (error: any) {
      console.error('Failed to load data:', error);
      showError('Failed to load broker accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [basket.basket_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNewAllocation = () => {
    setNewAllocations(prev => [...prev, {
      broker_id: '',
      client_id: '',
      lot_multiplier: 1
    }]);
  };

  const removeNewAllocation = (index: number) => {
    setNewAllocations(prev => prev.filter((_, i) => i !== index));
  };

  const updateNewAllocation = (index: number, field: keyof AllocationFormData, value: string | number) => {
    setNewAllocations(prev => prev.map((allocation, i) => 
      i === index ? { ...allocation, [field]: value } : allocation
    ));
  };

  const handleBrokerChange = (index: number, brokerId: string) => {
    const selectedBroker = brokerAccounts.find(b => `${b.broker_name}-${b.client_id}` === brokerId);
    if (selectedBroker) {
      updateNewAllocation(index, 'broker_id', selectedBroker.broker_name);
      updateNewAllocation(index, 'client_id', selectedBroker.client_id);
    }
  };

  const validateAllocations = (): boolean => {
    // Check if all allocations are complete
    for (const allocation of newAllocations) {
      if (!allocation.broker_id || !allocation.client_id || allocation.lot_multiplier <= 0) {
        showError('Please complete all allocation details with valid lot multipliers (> 0)');
        return false;
      }
    }

    // Check for duplicate broker-client combinations
    const combinations = newAllocations.map(a => `${a.broker_id}-${a.client_id}`);
    const uniqueCombinations = new Set(combinations);
    if (combinations.length !== uniqueCombinations.size) {
      showError('Duplicate broker-client combinations are not allowed');
      return false;
    }

    // Check against existing allocations
    if (Array.isArray(existingAllocations)) {
      for (const newAlloc of newAllocations) {
        const exists = existingAllocations.some(existing =>
          existing.broker_id === newAlloc.broker_id && existing.client_id === newAlloc.client_id
        );
        if (exists) {
          showError(`Allocation already exists for ${newAlloc.broker_id} - ${newAlloc.client_id}`);
          return false;
        }
      }
    }

    return true;
  };

  const handleDeleteAllocation = async (allocation: BasketBrokerAllocation) => {
    try {
      setSaving(true);

      await allocationService.deleteAllocation(basket.basket_id, allocation.allocation_id);

      showSuccess(`Successfully deleted allocation for ${allocation.broker_id} - ${allocation.client_id}`);

      // Remove from state locally instead of full reload - much faster!
      setExistingAllocations(prev =>
        prev.filter(a => a.allocation_id !== allocation.allocation_id)
      );

    } catch (error: any) {
      console.error('Failed to delete allocation:', error);
      showError(error.message || 'Failed to delete allocation. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (allocation: BasketBrokerAllocation) => {
    setEditingAllocation(allocation.allocation_id);
    setEditLotMultiplier(allocation.lot_multiplier);
  };

  const handleCancelEdit = () => {
    setEditingAllocation(null);
    setEditLotMultiplier(1);
  };

  const handleSaveEdit = async (allocation: BasketBrokerAllocation) => {
    try {
      setSaving(true);

      if (editLotMultiplier <= 0) {
        showError('Lot multiplier must be greater than 0');
        return;
      }

      await allocationService.updateAllocation(basket.basket_id, allocation.allocation_id, {
        status: allocation.status,
        lot_multiplier: editLotMultiplier
      });

      showSuccess(`Successfully updated lot multiplier for ${allocation.broker_id} - ${allocation.client_id}`);

      // Update state locally
      setExistingAllocations(prev =>
        prev.map(a =>
          a.allocation_id === allocation.allocation_id
            ? { ...a, lot_multiplier: editLotMultiplier }
            : a
        )
      );

      // Exit edit mode
      setEditingAllocation(null);
      setEditLotMultiplier(1);

    } catch (error: any) {
      console.error('Failed to update lot multiplier:', error);
      showError(error.message || 'Failed to update lot multiplier. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAllocationStatus = async (allocation: BasketBrokerAllocation) => {
    try {
      setSaving(true);

      const newStatus = allocation.status === ALLOCATION_STATUS.ACTIVE ? ALLOCATION_STATUS.INACTIVE : ALLOCATION_STATUS.ACTIVE;

      await allocationService.updateAllocation(basket.basket_id, allocation.allocation_id, {
        status: newStatus,
        lot_multiplier: allocation.lot_multiplier
      });

      showSuccess(`Successfully ${newStatus === ALLOCATION_STATUS.ACTIVE ? 'enabled' : 'disabled'} allocation for ${allocation.broker_id} - ${allocation.client_id}`);

      // Update state locally instead of full reload - much faster!
      setExistingAllocations(prev =>
        prev.map(a =>
          a.allocation_id === allocation.allocation_id
            ? { ...a, status: newStatus }
            : a
        )
      );

    } catch (error: any) {
      console.error('Failed to update allocation status:', error);
      showError(error.message || 'Failed to update allocation status. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllocations = async () => {
    try {
      if (newAllocations.length === 0) {
        showError('Please add at least one broker allocation');
        return;
      }

      if (!validateAllocations()) {
        return;
      }

      setSaving(true);

      const createData: CreateAllocation = {
        allocations: newAllocations
      };

      // Create basket allocations using the allocation service
      await allocationService.createBasketAllocations(basket.basket_id, createData);

      showSuccess(`Successfully allocated basket to ${newAllocations.length} broker account(s)`);
      setNewAllocations([]);

      // Refresh the allocations list to show newly created ones
      try {
        const updatedAllocations = await allocationService.getBasketAllocations(basket.basket_id);
        setExistingAllocations(updatedAllocations);
      } catch (error) {
        console.warn('Failed to refresh allocations after create, will reload on next visit');
      }

      // Don't automatically switch tabs - let user stay on allocation tab to see their new allocations

    } catch (error: any) {
      console.error('Failed to create allocations:', error);
      showError(error.message || 'Failed to create allocations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const calculateTotalMultiplier = (): number => {
    const newTotal = newAllocations.reduce((sum, allocation) => sum + allocation.lot_multiplier, 0);
    const existingTotal = Array.isArray(existingAllocations)
      ? existingAllocations.reduce((sum, allocation) => sum + allocation.lot_multiplier, 0)
      : 0;
    return newTotal + existingTotal;
  };

  // Smart cap on lot_multiplier — `lot_multiplier × max(leg.lots in basket) ≤ MAX_EFFECTIVE_LOTS_PER_LEG`.
  // Falls back to backend's hard 250 when basket has no strategies yet.
  const computedMaxMultiplier = maxLegLots > 0
    ? Math.max(1, Math.floor(MAX_EFFECTIVE_LOTS_PER_LEG / maxLegLots))
    : 250;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Allocations</CardTitle>
        </CardHeader>
        <CardContent>
          <LoadingSpinner message="Loading broker accounts..." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle>Allocations</CardTitle>
            <Badge variant="info" size="sm">{existingAllocations.length}</Badge>
            {calculateTotalMultiplier() > 0 && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                · <span className={`font-semibold ${wl ? 'text-zebu-600' : 'text-blue-600'}`}>{calculateTotalMultiplier()}</span> lots
              </span>
            )}
          </div>
          <Button
            onClick={addNewAllocation}
            leftIcon={<Plus className="h-4 w-4" />}
            size="sm"
            className="whitespace-nowrap self-start sm:self-auto"
          >
            Add Allocation
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">

      {/* Existing Allocations — edge-to-edge like StrategyRowView */}
      {Array.isArray(existingAllocations) && existingAllocations.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700">
          {/* Desktop header */}
          <div className="hidden md:flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 rounded-t-lg">
            <div className="w-10 shrink-0" />
            <div className="flex-1 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Broker</div>
            <div className="w-32 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Client ID</div>
            <div className="w-44 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Lot Multiplier</div>
            <div className="w-24 shrink-0 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</div>
            <div className="w-24 shrink-0" />
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {existingAllocations.map((allocation, index) => (
              <div
                key={`${allocation.broker_id}-${allocation.client_id}`}
                className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {/* Mobile layout */}
                <div className="md:hidden flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">
                        {allocation.broker_id.charAt(0).toUpperCase() + allocation.broker_id.slice(1)}
                      </span>
                      <Badge variant="info" size="sm">{allocation.client_id}</Badge>
                    </div>
                    <EnableToggle
                      enabled={allocation.status === ALLOCATION_STATUS.ACTIVE}
                      loading={saving}
                      disabled={editingAllocation !== null}
                      onChange={() => handleToggleAllocationStatus(allocation)}
                      size="md"
                      ariaLabel={allocation.status === ALLOCATION_STATUS.ACTIVE ? 'Disable allocation' : 'Enable allocation'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Lots:</span>
                      {editingAllocation === allocation.allocation_id ? (
                        <>
                          <Input
                            type="number"
                            min="1"
                            max={computedMaxMultiplier}
                            value={editLotMultiplier}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              setEditLotMultiplier(Number.isNaN(n) ? 1 : Math.max(1, Math.min(computedMaxMultiplier, n)));
                            }}
                            className="w-20 h-8 text-sm"
                          />
                          <button onClick={() => handleSaveEdit(allocation)} disabled={saving}
                            className="p-1 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50" title="Save">
                            <Save className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={handleCancelEdit} disabled={saving}
                            className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50" title="Cancel">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className={`font-bold text-sm ${wl ? 'text-zebu-600' : 'text-blue-600'}`}>{allocation.lot_multiplier}</span>
                          <button onClick={() => handleStartEdit(allocation)} disabled={saving || editingAllocation !== null}
                            className={`p-0.5 rounded text-gray-400 ${wl ? 'hover:text-zebu-600' : 'hover:text-blue-600'} transition-colors disabled:opacity-30`}
                            title="Edit lot multiplier"
                            aria-label="Edit lot multiplier">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDeleteAllocation(allocation)} disabled={saving || editingAllocation !== null}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden md:flex items-center gap-3 px-4 py-3.5">
                  {/* # */}
                  <div className="w-10 shrink-0 flex justify-center">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold">
                      {index + 1}
                    </span>
                  </div>
                  {/* Broker */}
                  <div className="flex-1 text-sm font-semibold text-gray-900 dark:text-white">
                    {allocation.broker_id.charAt(0).toUpperCase() + allocation.broker_id.slice(1)}
                  </div>
                  {/* Client ID */}
                  <div className="w-32 shrink-0">
                    <Badge variant="info" size="sm">{allocation.client_id}</Badge>
                  </div>
                  {/* Lot Multiplier */}
                  <div className="w-44 shrink-0">
                    {editingAllocation === allocation.allocation_id ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          type="number"
                          min="1"
                          max={computedMaxMultiplier}
                          value={editLotMultiplier}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setEditLotMultiplier(Number.isNaN(n) ? 1 : Math.max(1, Math.min(computedMaxMultiplier, n)));
                          }}
                          className="w-20 h-8 text-sm"
                        />
                        <button onClick={() => handleSaveEdit(allocation)} disabled={saving}
                          className="p-1 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50" title="Save">
                          <Save className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={handleCancelEdit} disabled={saving}
                          className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50" title="Cancel">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-bold ${wl ? 'text-zebu-600' : 'text-blue-600'}`}>{allocation.lot_multiplier}</span>
                        <button onClick={() => handleStartEdit(allocation)} disabled={saving || editingAllocation !== null}
                          className={`p-0.5 rounded text-gray-400 ${wl ? 'hover:text-zebu-600' : 'hover:text-blue-600'} transition-colors disabled:opacity-30`}
                          title="Edit lot multiplier"
                          aria-label="Edit lot multiplier">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Status */}
                  <div className="w-24 shrink-0">
                    <EnableToggle
                      enabled={allocation.status === ALLOCATION_STATUS.ACTIVE}
                      loading={saving}
                      disabled={editingAllocation !== null}
                      onChange={() => handleToggleAllocationStatus(allocation)}
                      size="md"
                      ariaLabel={allocation.status === ALLOCATION_STATUS.ACTIVE ? 'Disable allocation' : 'Enable allocation'}
                    />
                  </div>
                  {/* Actions */}
                  <div className="w-24 shrink-0 flex items-center justify-end gap-1">
                    <button onClick={() => handleDeleteAllocation(allocation)} disabled={saving || editingAllocation !== null}
                      className="p-1.5 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Allocations */}
      {newAllocations.length > 0 && (
        <div className="px-6 py-4 space-y-3 border-t border-gray-200 dark:border-gray-700">
          {newAllocations.map((allocation, index) => (
            <div key={index} className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Broker Account</label>
                <select
                  value={`${allocation.broker_id}-${allocation.client_id}`}
                  onChange={(e) => handleBrokerChange(index, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">Select broker account...</option>
                  {brokerAccounts.map(broker => (
                    <option key={broker.client_id} value={`${broker.broker_name}-${broker.client_id}`}>
                      {broker.broker_name.charAt(0).toUpperCase() + broker.broker_name.slice(1)} - {broker.client_id} (₹{broker.capital.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-28">
                <label className="block text-sm font-medium mb-1">Lots</label>
                {(() => {
                  // Cap multiplier so lot_multiplier × maxLegLots ≤ MAX_EFFECTIVE_LOTS_PER_LEG.
                  // Falls back to 250 (backend max) when basket has no strategies yet.
                  const computedMax = maxLegLots > 0
                    ? Math.max(1, Math.floor(MAX_EFFECTIVE_LOTS_PER_LEG / maxLegLots))
                    : 250;
                  return (
                    <>
                      <Input
                        type="number"
                        min="1"
                        max={computedMax}
                        step="1"
                        value={allocation.lot_multiplier}
                        onChange={(e) => {
                          const n = parseInt(e.target.value, 10);
                          updateNewAllocation(
                            index,
                            'lot_multiplier',
                            Number.isNaN(n) ? 1 : Math.max(1, Math.min(computedMax, n)),
                          );
                        }}
                        placeholder="1"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Multiplied with each leg's lots
                        {maxLegLots > 0 && (
                          <> · max {computedMax} ({MAX_EFFECTIVE_LOTS_PER_LEG} ÷ {maxLegLots} largest leg lots)</>
                        )}
                      </p>
                    </>
                  );
                })()}
              </div>
              <button
                onClick={() => removeNewAllocation(index)}
                className="p-2 rounded-lg text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}

          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-gray-500">
              {newAllocations.length} new · total lots{' '}
              <span className={`font-bold ${wl ? 'text-zebu-600' : 'text-blue-600'}`}>
                {newAllocations.reduce((sum, a) => sum + a.lot_multiplier, 0)}
              </span>
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNewAllocations([])}
                disabled={saving}
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAllocations}
                disabled={saving}
                leftIcon={saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : undefined}
                className="whitespace-nowrap"
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {existingAllocations.length === 0 && newAllocations.length === 0 && (
        <div className="px-6 py-12 text-center text-gray-500">
          <p className="text-sm">No broker allocations yet. Click "Add Allocation" to get started.</p>
        </div>
      )}
      </CardContent>
    </Card>
  );
};

export default BasketAllocation;